import { ClientMessage, GameAction, ServerMessage } from '../types'
import { wsClient } from './websocketClient'

type RTCHandlers = {
  onOpen?: () => void
  onAction?: (action: GameAction) => void
  onClose?: () => void
  onP2PLost?: () => void
  onP2PRestored?: (latencyMs: number) => void
}

// Buffer threshold for detecting potential overflow
const BUFFER_HIGH_WATER_MARK = 16384
const HEARTBEAT_INTERVAL_MS = 2000
const HEARTBEAT_TIMEOUT_MS = 6500

class WebRTCClient {
  private pc: RTCPeerConnection | null = null
  private dc: RTCDataChannel | null = null
  private connected = false
  private isHost = false
  private handlers: RTCHandlers = {}
  // Queue for messages when channel is temporarily unavailable
  private pendingMessages: GameAction[] = []
  // Track data channel ready state separately from connection
  private dcReady = false
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private lastPongAt = 0
  private p2pLost = false
  private pendingIceCandidates: RTCIceCandidateInit[] = []

  init(isHost: boolean) {
    this.close()
    this.isHost = isHost
    const iceServers: RTCIceServer[] = []
    const cnTurnUrl = (import.meta as any).env?.VITE_TURN_URL_CN
    const cnTurnUser = (import.meta as any).env?.VITE_TURN_USERNAME_CN
    const cnTurnPass = (import.meta as any).env?.VITE_TURN_PASSWORD_CN
    const turnUrl = (import.meta as any).env?.VITE_TURN_URL
    const turnUser = (import.meta as any).env?.VITE_TURN_USERNAME
    const turnPass = (import.meta as any).env?.VITE_TURN_PASSWORD
    iceServers.push({ urls: ['stun:stun.l.google.com:19302'] })
    if (turnUrl && turnUser && turnPass) iceServers.push({ urls: [turnUrl], username: turnUser, credential: turnPass })
    if (cnTurnUrl && cnTurnUser && cnTurnPass) iceServers.push({ urls: [cnTurnUrl], username: cnTurnUser, credential: cnTurnPass })
    this.pc = new RTCPeerConnection({ iceServers })
    this.pc.onicecandidate = (ev) => {
      if (ev.candidate) wsClient.send({ type: 'SIGNAL_ICE', roomCode: this.getRoomCode(), candidate: ev.candidate } as ClientMessage)
    }
    this.pc.onconnectionstatechange = () => {
      if (!this.pc) return
      if (this.pc.connectionState === 'connected') { this.connected = true; this.handlers.onOpen && this.handlers.onOpen() }
      if (this.pc.connectionState === 'closed' || this.pc.connectionState === 'failed' || this.pc.connectionState === 'disconnected') {
        this.connected = false
        this.markP2PLost()
        this.handlers.onClose && this.handlers.onClose()
      }
    }
    if (isHost) {
      this.dc = this.pc.createDataChannel('game', { ordered: true })
      this.attachDc(this.dc)
      this.pc.createOffer().then(offer => {
        return this.pc!.setLocalDescription(offer).then(() => {
          wsClient.send({ type: 'SIGNAL_OFFER', roomCode: this.getRoomCode(), sdp: offer } as ClientMessage)
        })
      })
    } else {
      this.pc.ondatachannel = (ev) => {
        this.dc = ev.channel
        this.attachDc(this.dc)
      }
    }
  }

  handleSignal(message: ServerMessage) {
    if (!this.pc) return
    if (message.type === 'SIGNAL_OFFER' && !this.isHost) {
      const desc = new RTCSessionDescription(message.sdp)
      this.pc.setRemoteDescription(desc).then(() => {
        this.flushPendingIceCandidates()
        return this.pc!.createAnswer()
      }).then(answer => {
        return this.pc!.setLocalDescription(answer).then(() => {
          wsClient.send({ type: 'SIGNAL_ANSWER', roomCode: this.getRoomCode(), sdp: answer } as ClientMessage)
        })
      }).catch(err => console.warn('[WebRTC] Failed to handle offer:', err))
    } else if (message.type === 'SIGNAL_ANSWER' && this.isHost) {
      const desc = new RTCSessionDescription(message.sdp)
      this.pc.setRemoteDescription(desc)
        .then(() => this.flushPendingIceCandidates())
        .catch(err => console.warn('[WebRTC] Failed to handle answer:', err))
    } else if (message.type === 'SIGNAL_ICE') {
      if (!this.pc.remoteDescription) {
        this.pendingIceCandidates.push(message.candidate)
        return
      }
      this.pc.addIceCandidate(message.candidate).catch(err => console.warn('[WebRTC] Failed to add ICE candidate:', err))
    }
  }

  private flushPendingIceCandidates() {
    if (!this.pc || !this.pc.remoteDescription) return
    const candidates = this.pendingIceCandidates.splice(0)
    candidates.forEach(candidate => {
      this.pc!.addIceCandidate(candidate).catch(err => console.warn('[WebRTC] Failed to add queued ICE candidate:', err))
    })
  }

  private attachDc(dc: RTCDataChannel | null) {
    if (!dc) return
    dc.bufferedAmountLowThreshold = Math.floor(BUFFER_HIGH_WATER_MARK / 2)
    dc.onbufferedamountlow = () => this.drainQueue()
    dc.onopen = () => {
      this.connected = true
      this.dcReady = true
      this.lastPongAt = Date.now()
      this.p2pLost = false
      this.handlers.onOpen && this.handlers.onOpen()
      this.startHeartbeat()
      this.drainQueue()
    }
    dc.onclose = () => {
      this.connected = false
      this.dcReady = false
      this.stopHeartbeat()
      this.markP2PLost()
      this.handlers.onClose && this.handlers.onClose()
    }
    dc.onerror = () => { }
    dc.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data)
        if (msg && msg.type === 'GAME_ACTION') {
          this.handlers.onAction && this.handlers.onAction(msg.action as GameAction)
        } else if (msg && msg.type === 'P2P_PING') {
          this.sendControl({ type: 'P2P_PONG', timestamp: msg.timestamp })
        } else if (msg && msg.type === 'P2P_PONG') {
          const now = Date.now()
          this.lastPongAt = now
          if (this.p2pLost) {
            this.p2pLost = false
            const latencyMs = typeof msg.timestamp === 'number' ? Math.max(0, now - msg.timestamp) : 0
            this.handlers.onP2PRestored && this.handlers.onP2PRestored(latencyMs)
          }
        }
      } catch { }
    }
  }

  private sendControl(message: unknown) {
    if (!this.dc || !this.dcReady) return
    try {
      this.dc.send(JSON.stringify(message))
    } catch {
      this.markP2PLost()
    }
  }

  private startHeartbeat() {
    this.stopHeartbeat()
    this.lastPongAt = Date.now()
    this.heartbeatTimer = setInterval(() => {
      if (!this.dc || !this.dcReady) return
      const now = Date.now()
      if (now - this.lastPongAt > HEARTBEAT_TIMEOUT_MS) {
        this.markP2PLost()
      }
      this.sendControl({ type: 'P2P_PING', timestamp: now })
    }, HEARTBEAT_INTERVAL_MS)
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private markP2PLost() {
    if (this.p2pLost) return
    this.p2pLost = true
    this.handlers.onP2PLost && this.handlers.onP2PLost()
  }

  private drainQueue() {
    if (!this.dc || !this.dcReady) return
    while (this.pendingMessages.length > 0) {
      if (this.dc.bufferedAmount > BUFFER_HIGH_WATER_MARK) {
        break
      }
      const action = this.pendingMessages.shift()!
      this.dc.send(JSON.stringify({ type: 'GAME_ACTION', action }))
    }
  }

  isConnected() { return this.connected && this.dcReady }
  isP2PHealthy() { return this.isConnected() && !this.p2pLost }

  /**
   * Send an action via WebRTC data channel.
   * Returns whether the action was sent immediately, queued for later, or failed.
   */
  sendAction(action: GameAction): 'sent' | 'queued' | 'failed' {
    if (!this.dc || !this.dcReady) {
      this.pendingMessages.push(action)
      return 'queued'
    }

    if (this.dc.bufferedAmount > BUFFER_HIGH_WATER_MARK) {
      this.pendingMessages.push(action)
      return 'queued'
    }

    try {
      this.dc.send(JSON.stringify({ type: 'GAME_ACTION', action }))
      return 'sent'
    } catch {
      this.pendingMessages.push(action)
      return 'failed'
    }
  }

  /** Check if there are queued messages that haven't been sent yet */
  hasPendingMessages(): boolean {
    return this.pendingMessages.length > 0
  }

  close() {
    this.stopHeartbeat()
    this.pendingMessages = []
    this.pendingIceCandidates = []
    this.dcReady = false
    this.connected = false
    this.p2pLost = false
    if (this.dc) {
      try { this.dc.close() } catch { }
      this.dc = null
    }
    if (this.pc) {
      try { this.pc.close() } catch { }
      this.pc = null
    }
    this.handlers = {}
  }

  setHandlers(h: RTCHandlers) { this.handlers = h }

  private getRoomCode(): string { return (window as any).__ROOM_CODE__ || '' }
  setRoomCode(roomCode: string) { (window as any).__ROOM_CODE__ = roomCode }
}

export const webrtcClient = new WebRTCClient()
